import { Component } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Store, Select } from '@ngxs/store';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import {
    ButtonColors,
    ButtonModule,
    LayerService,
    LayerSize,
    NumberfieldModule,
    TextfieldModule,
} from '@ed---interne/ng-uui-components';
import { PhaseModel, ProjectModel } from '../../../project.models';
import { ProjectsState } from '../../../project.state';
import { CommonModule } from '@angular/common';
import { SharedModule } from 'src/app/shared/shared.module';
import { Router, RouterModule } from '@angular/router';
import { Observable } from 'rxjs';
import { DisplayType } from 'src/app/shared/components/ed-table/ed-table.component';
import { MatTableDataSource } from '@angular/material/table';
import { LayerPopupOptions } from 'src/app/shared/all.constants';
import { PhaseCreateComponent } from './phase-create/phase-create.component';

@UntilDestroy()
@Component({
    selector: 'project-general',
    templateUrl: './project-general.component.html',
    styleUrls: ['./project-general.component.scss'],
    standalone: true,
    schemas: [],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        SharedModule,
        TextfieldModule,
        ButtonModule,
        NumberfieldModule,
        RouterModule,
    ],
    providers: [],
})
export class ProjectGeneralComponent {
    @Select(ProjectsState.projectWithDetail)
    public project$!: Observable<ProjectModel>;
    public project: ProjectModel | undefined;

    public buttonColors = ButtonColors;

    public phasesData = new MatTableDataSource<{}>([]);
    public phasesColumns = [
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Code',
            objectKey: 'code',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Label',
            objectKey: 'label',
            bold: true,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Catégorie R&D',
            objectKey: 'researchCategoryLabel',
            bold: false,
        },
        {
            displayType: DisplayType.NUMBER,
            objectDisplayName: 'Jours passés',
            objectKey: 'daysDeclared',
            bold: false,
        },
    ];

    public usersData = new MatTableDataSource<{}>([]);
    public usersColumns = [
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Prénom',
            objectKey: 'firstname',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Nom',
            objectKey: 'lastname',
            bold: true,
        },
        {
            displayType: DisplayType.NUMBER,
            objectDisplayName: 'Jours passés',
            objectKey: 'daysDeclared',
            bold: false,
        },
    ];

    constructor(
        private readonly router: Router,
        private readonly layerService: LayerService,
    ) {
        this.project$.pipe(untilDestroyed(this)).subscribe((project: ProjectModel) => {
            if (!project) return;
            if (project.estimatedCloseDate)
                project.estimatedCloseDate = new Date(project.estimatedCloseDate);
            this.project = project;
            this.phasesData = new MatTableDataSource<{}>(
                project.phases?.map((phase: PhaseModel) => ({
                    ...phase,
                    researchCategoryLabel: phase.researchCategory?.label,
                })),
            );
            this.generateUsersData();
        });
    }

    public generateUsersData() {
        if (!this.project || !this.project.phases) return;

        const usersData: any[] = [];
        for (const phase of this.project.phases) {
            for (const entry of phase.dailyTimeEntries) {
                const user = entry.user;
                if (!user) continue;
                const exist = usersData.find((u) => u.id === user.id);
                if (exist) {
                    exist.daysDeclared += entry.percentOfDay;
                } else {
                    usersData.push({
                        id: user.id,
                        firstname: user.firstname,
                        lastname: user.lastname,
                        daysDeclared: entry.percentOfDay || 0,
                    });
                }
            }
        }

        if (this.project.legacyReportedDays && this.project.legacyReportedDays > 0) {
            usersData.push({
                id: null,
                firstname: 'Import',
                lastname: 'ASA',
                daysDeclared: this.project.legacyReportedDays,
            });
        }
        this.usersData = new MatTableDataSource<{}>(usersData);
    }

    public addPhase() {
        if (!this.project) return;
        const header = 'Modifier la phase';
        this.layerService.show(
            PhaseCreateComponent,
            { header },
            { size: LayerSize.Medium, withClosePopup: LayerPopupOptions.Creating },
        );
    }

    public goToPhase(phaseId: any) {
        if (!this.project) return;
        const code = this.project.code;
        const phaseCode = this.project?.phases?.find((ph) => ph.id === phaseId)?.code;
        if (!phaseCode) return;

        this.router.navigate([`/main/projects/${code}/phase/${phaseCode}`]);
    }

    public goToContributor(userId: any) {
        if (!userId) return;
        this.router.navigate([`/main/timesheet/contributor/${userId}`]);
    }
}
